/**
 * Authentication context for Kalpa Niyama.
 *
 * Manages user state on top of the trust-tiered session model: a short-lived
 * access token held IN MEMORY (never localStorage) plus a rotating refresh
 * token persisted by the token manager. Recovery from expiry is graceful by
 * design — a failed silent refresh opens a modal re-auth dialog OVER the
 * current page (``reauthRequired``) without clearing ``user``, because
 * App.tsx swaps to the login routes the moment ``user`` is null and that
 * would destroy in-progress form state.
 *
 * For documentation, see:
 * - docs/technical/FRONTEND_ARCHITECTURE.md#authentication-system
 * - docs/technical/BACKEND_ARCHITECTURE.md#authentication
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import axios from 'axios';
import { Alert, Button, Snackbar } from '@mui/material';
import {
  SessionInfo,
  TokenPairResponse,
  TrustTier,
  TIER_TTL_OPTIONS,
  TRUST_TIER_HINT_KEY,
  getAccessExpiresAt,
  getSessionEpoch,
  getStoredRefreshToken,
  installAuthInterceptor,
  refreshAccessToken,
  replayQueue,
  rejectQueue,
  resetForLogout,
  setAccessToken,
  REFRESH_TOKEN_KEY,
} from '../auth/tokenManager';
import SessionReauthDialog from '../components/SessionReauthDialog';

interface User {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  is_active: boolean;
  created_at: string;
}

export interface LoginOptions {
  trustTier?: TrustTier;
  ttlMinutes?: number;
}

export interface TrustTierHint {
  tier: TrustTier;
  ttlMinutes?: number;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, options?: LoginOptions) => Promise<void>;
  register: (email: string, username: string, password: string, fullName?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  /** The current session as reported by the last login/refresh. */
  sessionInfo: SessionInfo | null;
  /** True while the modal re-auth dialog should cover the page. */
  reauthRequired: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TRUST_TIERS: TrustTier[] = ['untrusted', 'moderate', 'trusted'];

/** Non-sensitive per-browser default for the login form's tier selector. */
export const loadTrustTierHint = (): TrustTierHint => {
  const fallback: TrustTierHint = { tier: 'untrusted' };
  try {
    const raw = localStorage.getItem(TRUST_TIER_HINT_KEY);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw);
    if (!TRUST_TIERS.includes(parsed?.tier)) {
      return fallback;
    }
    const hint: TrustTierHint = { tier: parsed.tier };
    const choices = TIER_TTL_OPTIONS[hint.tier].map((o) => o.minutes);
    if (typeof parsed.ttlMinutes === 'number' && choices.includes(parsed.ttlMinutes)) {
      hint.ttlMinutes = parsed.ttlMinutes;
    }
    return hint;
  } catch {
    return fallback;
  }
};

const saveTrustTierHint = (hint: TrustTierHint): void => {
  try {
    localStorage.setItem(TRUST_TIER_HINT_KEY, JSON.stringify(hint));
  } catch {
    // Ignore storage failures; the hint is a convenience only.
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [expiryWarning, setExpiryWarning] = useState(false);
  const [securityNotice, setSecurityNotice] = useState<string | null>(null);

  const userRef = useRef<User | null>(null);
  userRef.current = user;
  const proactiveTimerRef = useRef<number | null>(null);
  const warningTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (proactiveTimerRef.current !== null) {
      window.clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = null;
    }
    if (warningTimerRef.current !== null) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const scheduleTimers = useCallback(
    (pair: TokenPairResponse) => {
      clearTimers();
      // Pre-emptive refresh at 80% of access-token life, visible tabs only —
      // hidden tabs are covered by the visibilitychange handler instead.
      const proactiveMs = Math.max(pair.expires_in * 0.8 * 1000, 5000);
      proactiveTimerRef.current = window.setTimeout(() => {
        if (document.visibilityState === 'visible') {
          refreshAccessToken().catch(() => {
            // A hard failure surfaces through the interceptor on the next
            // real request; nothing to do here.
          });
        }
      }, proactiveMs);
      if (pair.session.trust_tier === 'untrusted') {
        const warnMs = (pair.refresh_expires_in - 120) * 1000;
        if (warnMs > 0) {
          warningTimerRef.current = window.setTimeout(() => setExpiryWarning(true), warnMs);
        }
      }
    },
    [clearTimers],
  );

  const handleTokenPair = useCallback(
    (pair: TokenPairResponse) => {
      // The token manager already persisted the refresh token for refresh
      // responses; login responses land here first, so persist defensively.
      setAccessToken(pair.access_token, pair.expires_in);
      try {
        localStorage.setItem(REFRESH_TOKEN_KEY, pair.refresh_token);
      } catch {
        // Storage unavailable — session won't survive a reload, still usable.
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${pair.access_token}`;
      setSessionInfo(pair.session);
      setExpiryWarning(false);
      scheduleTimers(pair);
    },
    [scheduleTimers],
  );
  const handleTokenPairRef = useRef(handleTokenPair);
  handleTokenPairRef.current = handleTokenPair;

  const clearLocalAuthState = useCallback(() => {
    resetForLogout();
    clearTimers();
    setUser(null);
    setSessionInfo(null);
    setReauthRequired(false);
    setExpiryWarning(false);
    delete axios.defaults.headers.common['Authorization'];
  }, [clearTimers]);

  const logout = useCallback(() => {
    const refresh = getStoredRefreshToken();
    // Fire-and-forget server-side revocation; local state clears regardless.
    axios
      .post(`${API_BASE_URL}/api/auth/logout`, refresh ? { refresh_token: refresh } : {})
      .catch(() => {});
    clearLocalAuthState();
    // Legacy pre-session key hygiene. Deliberately surgical: preference keys
    // (kalpa-*) must survive logout.
    localStorage.removeItem('token');
  }, [clearLocalAuthState]);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const fetchUser = useCallback(async () => {
    const response = await axios.get(`${API_BASE_URL}/api/auth/me`);
    setUser(response.data);
  }, []);

  // Install the global interceptor once and bootstrap the session.
  useEffect(() => {
    installAuthInterceptor(axios, API_BASE_URL, {
      onReauthRequired: () => {
        if (userRef.current) {
          setReauthRequired(true);
        } else {
          // Not signed in yet (e.g. a failed boot) — fail the parked
          // requests; App will show the login page.
          rejectQueue(new Error('not_authenticated'));
        }
      },
      onHardLogout: () => {
        clearLocalAuthState();
        setSecurityNotice(
          'You were signed out because your session token was used elsewhere. Please sign in again.',
        );
      },
      onTokenRefreshed: (pair) => handleTokenPairRef.current(pair),
    });

    const bootstrap = async () => {
      // One-time adoption of the pre-session localStorage token: use it as
      // an in-memory access token, then delete the key forever. When it
      // expires (≤30 min) the re-auth dialog upgrades it to a real session.
      let legacy: string | null = null;
      try {
        legacy = localStorage.getItem('token');
        if (legacy) {
          localStorage.removeItem('token');
        }
      } catch {
        legacy = null;
      }
      try {
        if (legacy) {
          setAccessToken(legacy);
          axios.defaults.headers.common['Authorization'] = `Bearer ${legacy}`;
          await fetchUser();
        } else if (getStoredRefreshToken()) {
          await refreshAccessToken();
          await fetchUser();
        }
      } catch {
        // Bootstrap failed — remain signed out.
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Returning to the tab near/after expiry refreshes immediately — the
  // "left the app open on the desktop overnight" scenario.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !userRef.current) {
        return;
      }
      const expiresAt = getAccessExpiresAt();
      if (!expiresAt || Date.now() > expiresAt - 120_000) {
        refreshAccessToken().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Cross-tab: another tab logging out removes the shared refresh token.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_TOKEN_KEY) {
        return;
      }
      if (event.newValue === null && userRef.current) {
        clearLocalAuthState();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [clearLocalAuthState]);

  const login = useCallback(
    async (email: string, password: string, options?: LoginOptions) => {
      const hint = loadTrustTierHint();
      const tier = options?.trustTier ?? hint.tier;
      const ttlMinutes = options?.ttlMinutes ?? (options?.trustTier ? undefined : hint.ttlMinutes);
      const response = await axios.post(`${API_BASE_URL}/api/auth/login-json`, {
        email,
        password,
        trust_tier: tier,
        ttl_minutes: ttlMinutes ?? null,
      });
      const pair = response.data as TokenPairResponse;
      handleTokenPair(pair);
      saveTrustTierHint({ tier, ttlMinutes });
      await fetchUser();
    },
    [fetchUser, handleTokenPair],
  );

  const register = useCallback(
    async (email: string, username: string, password: string, fullName?: string) => {
      await axios.post(`${API_BASE_URL}/api/auth/register`, {
        email,
        username,
        password,
        full_name: fullName,
      });
      // Auto-login after registration
      await login(email, password);
    },
    [login],
  );

  /** Re-auth from the modal: new session, then replay every parked request. */
  const completeReauth = useCallback(
    async (password: string) => {
      const email = userRef.current?.email;
      if (!email) {
        return;
      }
      const epoch = getSessionEpoch();
      const hint = loadTrustTierHint();
      const response = await axios.post(`${API_BASE_URL}/api/auth/login-json`, {
        email,
        password,
        trust_tier: hint.tier,
        ttl_minutes: hint.ttlMinutes ?? null,
      });
      const pair = response.data as TokenPairResponse;
      if (epoch !== getSessionEpoch()) {
        // The user signed out while this login was in flight. Adopting the
        // new session would silently re-authenticate them; revoke the session
        // we just created instead of leaving it live and unreachable.
        axios
          .post(`${API_BASE_URL}/api/auth/logout`, { refresh_token: pair.refresh_token })
          .catch(() => {});
        return;
      }
      handleTokenPair(pair);
      setReauthRequired(false);
      replayQueue(pair.access_token);
    },
    [handleTokenPair],
  );

  const cancelReauth = useCallback(() => {
    setReauthRequired(false);
    logoutRef.current();
  }, []);

  const extendSession = useCallback(() => {
    setExpiryWarning(false);
    refreshAccessToken(true).catch(() => {});
  }, []);

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    loading,
    sessionInfo,
    reauthRequired,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionReauthDialog
        open={reauthRequired && user !== null}
        email={user?.email || ''}
        onSubmit={completeReauth}
        onSignOut={cancelReauth}
      />
      <Snackbar
        open={expiryWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={extendSession}>
              Extend
            </Button>
          }
          onClose={() => setExpiryWarning(false)}
        >
          This session expires in about 2 minutes.
        </Alert>
      </Snackbar>
      <Snackbar
        open={securityNotice !== null}
        autoHideDuration={12000}
        onClose={() => setSecurityNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSecurityNotice(null)}>
          {securityNotice}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
